/**
 * ─── Supabase Sync Component ──────────────────────────────────────────────────
 *
 * Handles all data synchronization between Supabase and the Zustand store:
 * 1. On mount: finds the user's team, fetches all data, populates the store
 * 2. Sets up real-time subscriptions for leads, messages, tasks, notifications
 * 3. Re-fetches when user changes
 *
 * When Supabase isn't configured (demo mode), this component is a no-op.
 */

import { useEffect, useState, useRef, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStore, type Lead, type TeamMember, type Task, type ChatChannel, type ChatMessage, type Buyer, type CoverageArea, type AppNotification, type TimelineEntry, type StatusHistoryEntry, type AIThread, type AIBotMessage } from '../store/useStore';
import { supabase, isSupabaseConfigured } from './supabase';
import { DeepSpaceLoader } from '../components/DeepSpaceLoader';

// ─── DB → Store Converters ────────────────────────────────────────────────────

function dbLeadToStore(row: Record<string, unknown>, timeline: TimelineEntry[], statusHistory: StatusHistoryEntry[]): Lead | null {
  if (!row || !row.id) return null;
  return {
    id: row.id as string,
    name: (row.name as string) || '',
    email: (row.email as string) || '',
    phone: (row.phone as string) || '',
    status: (row.status as Lead['status']) || 'new',
    source: (row.source as Lead['source']) || 'other',
    propertyAddress: (row.address as string) || '',
    propertyType: (row.property_type as Lead['propertyType']) || 'single-family',
    estimatedValue: Number(row.property_value) || 0,
    offerAmount: Number(row.offer_amount) || 0,
    lat: Number(row.lat) || 30.2672,
    lng: Number(row.lng) || -97.7431,
    notes: (row.notes as string) || '',
    assignedTo: (row.assigned_to as string) || '',
    createdAt: (row.created_at as string) || new Date().toISOString(),
    updatedAt: (row.updated_at as string) || new Date().toISOString(),
    probability: Number(row.probability) || 50,
    engagementLevel: Number(row.engagement_level) || 3,
    timelineUrgency: Number(row.timeline_urgency) || 3,
    competitionLevel: Number(row.competition_level) || 3,
    importSource: (row.import_source as string) || undefined,
    photos: (row.photos as string[]) || [],
    bedrooms: Number(row.bedrooms) || 0,
    bathrooms: Number(row.bathrooms) || 0,
    sqft: Number(row.sqft) || 0,
    lastSoldPrice: Number(row.last_sold_price) || undefined,
    lastSoldDate: (row.last_sold_date as string) || undefined,
    estimatedEquity: Number(row.estimated_equity) || undefined,
    recommendedOffer: Number(row.recommended_offer) || undefined,
    folder_name: (row.folder_name as string) || undefined,
    timeline,
    statusHistory,
    documents: [], // Will be populated by fetch if needed, or initialized as empty
  };
}

function dbTeamMemberToStore(row: Record<string, unknown>, profile: Record<string, unknown> | null): TeamMember | null {
  if (!row || (!row.user_id && !row.id)) return null;
  const name = (profile?.full_name as string) || (profile?.email as string)?.split('@')[0] || 'User';
  const initials = name.split(' ').map((n: string) => n[0]).join('').toUpperCase().slice(0, 2);
  return {
    id: (row.user_id as string) || (row.id as string),
    name,
    role: (profile?.role as string) || 'Member',
    email: (profile?.email as string) || '',
    phone: (profile?.phone as string) || '',
    avatar: (profile?.avatar_url as string) || initials,
    dealsCount: 0,
    revenue: 0,
    presenceStatus: ((row.status as string) || 'offline') as TeamMember['presenceStatus'],
    lastSeen: (row.last_seen as string) || new Date().toISOString(),
    customStatus: (row.custom_status as string) || '',
    teamRole: ((row.role as string) || 'member') as TeamMember['teamRole'],
  };
}

function dbTaskToStore(row: Record<string, unknown>): Task {
  return {
    id: row.id as string,
    title: (row.title as string) || '',
    description: (row.description as string) || '',
    assignedTo: (row.assigned_to as string) || '',
    dueDate: (row.due_date as string) || '',
    priority: ((row.priority as string) || 'medium') as Task['priority'],
    status: ((row.status as string) || 'todo') as Task['status'],
    createdBy: (row.created_by as string) || '',
    createdAt: (row.created_at as string) || new Date().toISOString(),
    completedAt: (row.completed_at as string) || null,
    leadId: (row.lead_id as string) || undefined,
  };
}

function dbChannelToStore(row: Record<string, unknown>, memberIds: string[]): ChatChannel {
  return {
    id: row.id as string,
    name: (row.name as string) || '',
    type: ((row.type as string) || 'group') as ChatChannel['type'],
    members: memberIds,
    description: (row.description as string) || '',
    avatar: (row.avatar as string) || '💬',
    createdAt: (row.created_at as string) || new Date().toISOString(),
    createdBy: (row.created_by as string) || '',
    lastMessageAt: (row.last_message_at as string) || new Date().toISOString(),
    pinnedMessageIds: [],
  };
}

function dbMessageToStore(row: Record<string, unknown>): ChatMessage {
  return {
    id: row.id as string,
    channelId: (row.channel_id as string) || '',
    senderId: (row.user_id as string) || '',
    senderName: (row.sender_name as string) || 'User',
    senderAvatar: ((row.sender_name as string) || 'U').split(' ').map((n: string) => n[0]).join('').toUpperCase().slice(0, 2),
    content: (row.content as string) || '',
    timestamp: (row.created_at as string) || new Date().toISOString(),
    type: ((row.type as string) || 'text') as ChatMessage['type'],
    mentions: (row.mentions as string[]) || [],
    reactions: [],
    replyToId: (row.reply_to_id as string) || null,
    attachments: (row.attachments as ChatMessage['attachments']) || [],
    edited: (row.edited as boolean) || false,
    readBy: [(row.user_id as string) || ''],
    deleted: (row.deleted as boolean) || false,
  };
}

function dbBuyerToStore(row: Record<string, unknown>): Buyer {
  const criteria = (row.criteria as Record<string, unknown>) || {};
  return {
    id: row.id as string,
    name: (row.name as string) || '',
    email: (row.email as string) || '',
    phone: (row.phone as string) || '',
    lat: Number(row.lat) || 30.27,
    lng: Number(row.lng) || -97.74,
    criteria: {
      propertyTypes: (criteria.propertyTypes as Buyer['criteria']['propertyTypes']) || ['single-family'],
      bedroomsMin: Number(criteria.bedroomsMin) || 2,
      bathroomsMin: Number(criteria.bathroomsMin) || 1,
      sqftMin: Number(criteria.sqftMin) || 1000,
      sqftMax: Number(criteria.sqftMax) || 3000,
      locationPreferences: (criteria.locationPreferences as string[]) || [],
    },
    budgetMin: Number(row.budget_min) || 0,
    budgetMax: Number(row.budget_max) || 0,
    active: row.active !== false,
    dealScore: Number(row.deal_score) || 50,
    notes: (row.notes as string) || '',
    createdAt: (row.created_at as string) || new Date().toISOString(),
  };
}

function dbCoverageAreaToStore(row: Record<string, unknown>): CoverageArea {
  let coords: [number, number][] = [];
  try {
    const raw = row.coordinates;
    if (typeof raw === 'string') coords = JSON.parse(raw);
    else if (Array.isArray(raw)) coords = raw as [number, number][];
  } catch { /* empty */ }
  return {
    id: row.id as string,
    name: (row.name as string) || '',
    coordinates: coords,
    color: (row.color as string) || '#3b82f6',
    opacity: Number(row.opacity) || 0.2,
    leadCount: 0,
    notes: (row.notes as string) || '',
    createdAt: (row.created_at as string) || new Date().toISOString(),
  };
}

function dbNotificationToStore(row: Record<string, unknown>): AppNotification {
  return {
    id: row.id as string,
    type: (row.type as AppNotification['type']) || 'system',
    title: (row.title as string) || '',
    message: (row.message as string) || '',
    timestamp: (row.created_at as string) || new Date().toISOString(),
    read: (row.read as boolean) || false,
    link: (row.link as string) || undefined,
  };
}

function dbTimelineToStore(row: Record<string, unknown>): TimelineEntry {
  return {
    id: row.id as string,
    type: ((row.type as string) || 'note') as TimelineEntry['type'],
    content: (row.content as string) || '',
    timestamp: (row.created_at as string) || new Date().toISOString(),
    user: (row.user_id as string) || 'System',
    metadata: (row.metadata as Record<string, string>) || undefined,
  };
}

function dbStatusHistoryToStore(row: Record<string, unknown>): StatusHistoryEntry {
  return {
    fromStatus: (row.from_status as StatusHistoryEntry['fromStatus']) || null,
    toStatus: (row.to_status as StatusHistoryEntry['toStatus']) || 'new',
    timestamp: (row.changed_at as string) || new Date().toISOString(),
    changedBy: (row.changed_by as string) || 'System',
  };
}

function dbAiThreadToStore(row: Record<string, unknown>): AIThread {
  return {
    id: row.id as string,
    title: (row.title as string) || 'New Conversation',
    createdAt: (row.created_at as string) || new Date().toISOString(),
    lastMessageAt: (row.last_message_at as string) || new Date().toISOString(),
    pinned: (row.pinned as boolean) || false,
  };
}

function dbAiMessageToStore(row: Record<string, unknown>): AIBotMessage {
  return {
    id: row.id as string,
    role: ((row.role as string) === 'user' ? 'user' : 'ai') as AIBotMessage['role'],
    content: (row.content as string) || '',
    timestamp: (row.timestamp as string) || new Date().toISOString(),
    intent: (row.intent as string) || undefined,
    data: row.data || undefined,
    systemLog: (row.system_log as string) || undefined,
    type: (row.type as AIBotMessage['type']) || undefined,
    originalPhrase: (row.original_phrase as string) || undefined,
  };
}

// ─── Main Sync Component ──────────────────────────────────────────────────────

export function SupabaseSync({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const currentUser = useStore((s) => s.currentUser);
  const teamId = useStore((s) => s.teamId);
  const dataLoaded = useStore((s) => s.dataLoaded);
  const [syncStatus, setSyncStatus] = useState('Connecting...');
  const [syncProgress, setSyncProgress] = useState(0);
  const [loading, setLoading] = useState(!useStore.getState().dataLoaded);
  const subscriptionsRef = useRef<Array<{ unsubscribe: () => void }>>([]);

  useEffect(() => {
    // Demo mode — no sync needed, data is already in store (seed data)
    if (!isSupabaseConfigured || !supabase || !currentUser) {
      setLoading(false);
      return;
    }

    // Have persisted data — show UI immediately without loading screen.
    // Still run a silent background refresh so data stays current.
    const hasCachedData = dataLoaded && useStore.getState().teamId && useStore.getState().team.length > 0;
    if (hasCachedData) {
      setLoading(false);
      // Fall through to loadAllData() — it runs silently (loading is already false)
    }

    console.log('[SupabaseSync] Initializing data sync...');
    setSyncProgress(5);

    let cancelled = false;

    async function loadAllData() {
      try {
        const userId = currentUser!.id;

        // ── Step 1: Find user's team ──────────────────────────────────
        setSyncStatus('Finding your team...');
        setSyncProgress(15);
        console.log('[SupabaseSync] Querying memberships for user:', userId);
        const { data: teamMemberships, error: membershipError } = await supabase!
          .from('team_members')
          .select('team_id, role, status, custom_status, last_seen, teams(*)')
          .eq('user_id', userId);

        if (membershipError) {
          console.error('[SupabaseSync] Error fetching memberships:', membershipError);
          throw membershipError;
        }

        if (cancelled) return;

        console.log('[SupabaseSync] Found memberships:', teamMemberships?.length || 0);
        if (!teamMemberships || teamMemberships.length === 0) {
          console.warn('[SupabaseSync] No teams found for user. Redirecting to team selection...');
          setSyncStatus('Onboarding required...');
          // Small delay for UX transition
          setTimeout(() => {
            useStore.setState({ dataLoaded: true, teamId: null });
            navigate('/team-selection', { replace: true });
          }, 1500);
          return;
        }

        // If user has multiple teams, prefer the one they joined via invite code
        const preferredTeamId = localStorage.getItem('wholescale-preferred-team');
        console.log('[SupabaseSync] Preferred team from storage:', preferredTeamId);
        let membership = teamMemberships[0];
        if (preferredTeamId) {
          const preferred = teamMemberships.find((m: any) => m.team_id === preferredTeamId);
          if (preferred) {
            membership = preferred;
          }
          // Clean up — only need this for the first load after joining
          // Keep it so refreshes also use the preferred team
        }

        const teamId = membership.team_id;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const teamData = (membership as any).teams as Record<string, unknown>;
        console.log('[SupabaseSync] Initializing with teamId:', teamId, 'Name:', teamData?.name);

        // Update team config
        useStore.setState({
          teamId,
          teamConfig: {
            id: teamId,
            name: (teamData?.name as string) || 'My Team',
            inviteCode: (teamData?.invite_code as string) || 'WS-000000',
            maxSeats: 10,
            createdAt: (teamData?.created_at as string) || new Date().toISOString(),
            createdBy: (teamData?.owner_id as string) || userId,
          },
        });
        console.log('[SupabaseSync] teamConfig updated in store');

        // ── Step 2: Fetch all data in parallel ────────────────────────
        setSyncStatus('Syncing Cloud infrastructure...');
        setSyncProgress(35);

        const TIMEOUT_MS = 10000;
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('SYNC_TIMEOUT')), TIMEOUT_MS)
        );

        const fetchAllData = async () => {
          const [
            leadsRes, teamMembersRes, tasksRes, channelsRes,
            buyersRes, coverageRes, notifRes, automationsRes, logsRes,
            aiThreadsRes, aiMessagesRes
          ] = await Promise.all([
            supabase!.from('leads').select('*, timeline_entries(*), status_history(*)').eq('team_id', teamId).order('updated_at', { ascending: false }),
            supabase!.from('team_members').select('*, profiles(*)').eq('team_id', teamId),
            supabase!.from('tasks').select('*').eq('team_id', teamId).order('due_date', { ascending: true }),
            supabase!.from('channels').select('*, channel_members(*)').eq('team_id', teamId).order('last_message_at', { ascending: false }),
            supabase!.from('buyers').select('*').eq('team_id', teamId),
            supabase!.from('coverage_areas').select('*').eq('team_id', teamId),
            supabase!.from('notifications').select('*').eq('user_id', userId).order('created_at', { ascending: false }).limit(50),
            supabase!.from('user_automations').select('*').eq('user_id', userId).order('updated_at', { ascending: false }),
            supabase!.from('automation_logs').select('id, status, trigger_event, executed_at, details, automation_id').eq('user_id', userId).order('executed_at', { ascending: false }).limit(20),
            supabase!.from('ai_threads').select('*').eq('user_id', userId).order('last_message_at', { ascending: false }),
            supabase!.from('ai_messages').select('*').eq('user_id', userId).order('timestamp', { ascending: true }),
          ]);

          return { leadsRes, teamMembersRes, tasksRes, channelsRes, buyersRes, coverageRes, notifRes, automationsRes, logsRes, aiThreadsRes, aiMessagesRes };
        };

        let results;
        try {
          results = await Promise.race([fetchAllData(), timeoutPromise]) as any;
        } catch (err: any) {
          console.warn('[SupabaseSync] Initial sync timed out or failed, proceeding with partial state:', err);
          if (err.message === 'SYNC_TIMEOUT') {
            // If we timed out, we force dataLoaded: true to unstick the UI
            useStore.setState({ dataLoaded: true });
            setSyncStatus('Sync taking longer than expected. Continuing...');
            return;
          }
          throw err;
        }

        const { leadsRes, teamMembersRes, tasksRes, channelsRes, buyersRes, coverageRes, notifRes, automationsRes, logsRes, aiThreadsRes, aiMessagesRes } = results;


        if (cancelled) return;
        setSyncStatus('Processing data...');
        setSyncProgress(70);

        // ── Step 4: Convert all data ─────────────────────────────────
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const leads: Lead[] = (leadsRes.data || [])
          .map((row: any) => {
            const timeline = (row.timeline_entries || []).map((e: any) => dbTimelineToStore(e));
            const statusHistory = (row.status_history || []).map((h: any) => dbStatusHistoryToStore(h));
            return dbLeadToStore(row, timeline, statusHistory);
          })
          .filter((l: Lead | null): l is Lead => l !== null);


        // Enrich team members with deal counts from leads
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const team: TeamMember[] = (teamMembersRes.data || [])
          .map((row: any) => {
            const profile = row.profiles || null;
            const member = dbTeamMemberToStore(row, profile);
            if (!member) return null;
            // Count deals assigned to this member
            member.dealsCount = leads.filter(l => l && (l.assignedTo === member.name || l.assignedTo === member.id)).length;
            member.revenue = leads
              .filter(l => l && (l.assignedTo === member.name || l.assignedTo === member.id) && l.status === 'closed-won')
              .reduce((sum, l) => sum + (Number(l.estimatedValue) || 0), 0);
            return member;
          })
          .filter((m: TeamMember | null): m is TeamMember => m !== null);

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const tasks: Task[] = (tasksRes.data || []).map((row: any) => dbTaskToStore(row));

        // Channels and messages (Parallelized)
        const channels: ChatChannel[] = [];
        const messages: Record<string, ChatMessage[]> = {};
        const unreadCounts: Record<string, number> & { sms: number } = { sms: 0 };

        const channelEntries = (channelsRes.data || []) as any[];
        await Promise.all(channelEntries.map(async (chRow) => {
          const chId = chRow.id as string;
          const memberIds = ((chRow.channel_members as Array<{ user_id: string }>) || []).map(cm => cm.user_id);
          channels.push(dbChannelToStore(chRow, memberIds));

          try {
            const { data: msgData } = await supabase!
              .from('messages')
              .select('*')
              .eq('channel_id', chId)
              .order('created_at', { ascending: true })
              .limit(50); // Reduced limit for faster initial load

            messages[chId] = (msgData || []).map((m: any) => dbMessageToStore(m));
          } catch (err) {
            console.error(`[SupabaseSync] Failed to fetch items for channel ${chId}:`, err);
            messages[chId] = [];
          }
          unreadCounts[chId] = 0;
        }));


        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const buyers: Buyer[] = (buyersRes.data || []).map((row: any) => dbBuyerToStore(row));
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const coverageAreas: CoverageArea[] = (coverageRes.data || []).map((row: any) => dbCoverageAreaToStore(row));
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const notifications: AppNotification[] = (notifRes.data || []).map((row: any) => dbNotificationToStore(row));

        const myAutomations = automationsRes.data || [];
        const recentLogs = (logsRes.data || []).map((log: any) => ({
          ...log,
          automation_name: myAutomations.find((a: any) => a.id === log.automation_id)?.name || 'Unknown',
        }));

        const aiThreads: AIThread[] = (aiThreadsRes?.data || []).map((row: any) => dbAiThreadToStore(row));
        const aiMessages: Record<string, AIBotMessage[]> = {};
        for (const msgRow of (aiMessagesRes?.data || [])) {
          const threadId = msgRow.thread_id as string;
          if (!aiMessages[threadId]) aiMessages[threadId] = [];
          aiMessages[threadId].push(dbAiMessageToStore(msgRow));
        }

        if (cancelled) return;

        // ── Step 5: Set all data in store at once ─────────────────────
        setSyncStatus('Ready!');
        setSyncProgress(95);

        useStore.setState({
          dataLoaded: true,
          leads,
          team,
          tasks,
          channels,
          messages,
          unreadCounts,
          currentChannelId: channels[0]?.id || null,
          buyers,
          coverageAreas,
          notifications,
          myAutomations,
          recentLogs,
          aiThreads,
          aiMessages,
          // Keep other state (theme, streaks, etc.)
        });

        // Data is now in store — advance to 100% THEN hide the loader.
        // This guarantees the UI renders with real data, not 0 nodes.
        setSyncProgress(100);
        await new Promise((resolve) => setTimeout(resolve, 800));
        setLoading(false);

        // ── Step 6: Set up real-time subscriptions ────────────────────
        setupRealtimeSubscriptions(teamId, userId);

        // ── Step 7: Update user's presence to online ──────────────────
        await supabase!.from('team_members')
          .update({ status: 'online', last_seen: new Date().toISOString() })
          .eq('team_id', teamId)
          .eq('user_id', userId);

      } catch (err) {
        console.error('SupabaseSync: Error loading data:', err);
        // Still let the app render — it'll just show empty data
        useStore.setState({ dataLoaded: true });
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadAllData();

    return () => {
      cancelled = true;
      // Cleanup subscriptions
      for (const sub of subscriptionsRef.current) {
        try { sub.unsubscribe(); } catch { /* ignore */ }
      }
      subscriptionsRef.current = [];
    };
  }, [currentUser?.id, teamId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Real-time subscription setup ────────────────────────────────────────────

  function setupRealtimeSubscriptions(teamId: string, userId: string) {
    if (!supabase) return;

    // Clean up existing subscriptions
    for (const sub of subscriptionsRef.current) {
      try { sub.unsubscribe(); } catch { /* ignore */ }
    }
    subscriptionsRef.current = [];

    // 1. Leads — real-time INSERT/UPDATE/DELETE
    const leadsChannel = supabase.channel(`sync-leads-${teamId}`)
      .on('postgres_changes', {
        event: '*', schema: 'public', table: 'leads',
        filter: `team_id=eq.${teamId}`,
      }, (payload: { eventType: string; new: any; old: any }) => {
        const { eventType, new: newRow, old: oldRow } = payload;
        const state = useStore.getState();

        if (eventType === 'INSERT' && newRow) {
          // Don't add if we already have it (local optimistic update)
          if (state.leads.find(l => l.id === newRow.id)) return;
          const lead = dbLeadToStore(newRow as Record<string, unknown>, [], []);
          if (lead) {
            useStore.setState({ leads: [...state.leads, lead] });
          }
        } else if (eventType === 'UPDATE' && newRow) {
          const updated = dbLeadToStore(newRow as Record<string, unknown>,
            state.leads.find(l => l.id === newRow.id)?.timeline || [],
            state.leads.find(l => l.id === newRow.id)?.statusHistory || [],
          );
          if (updated) {
            useStore.setState({
              leads: state.leads.map(l => l.id === newRow.id ? { ...l, ...updated } : l),
            });
          }
        } else if (eventType === 'DELETE' && oldRow) {
          useStore.setState({ leads: state.leads.filter(l => l.id !== oldRow.id) });
        }
      })
      .subscribe();

    subscriptionsRef.current.push({
      unsubscribe: () => supabase!.removeChannel(leadsChannel),
    });

    // 2. Messages — real-time INSERT/DELETE
    const messagesChannel = supabase.channel(`sync-messages-${teamId}`)
      .on('postgres_changes', {
        event: '*', schema: 'public', table: 'messages',
      }, (payload: { eventType: string; new: any; old: any }) => {
        const { eventType, new: newRow, old: oldRow } = payload;
        const state = useStore.getState();

        if (eventType === 'INSERT' && newRow) {
          const channelId = newRow.channel_id as string;
          const existingMsgs = state.messages[channelId] || [];

          // Don't add if we already have it (local optimistic update)
          if (existingMsgs.find(m => m.id === newRow.id)) return;

          // Don't add if it's from us (already added locally)
          if (newRow.user_id === userId) return;

          const msg = dbMessageToStore(newRow as Record<string, unknown>);
          useStore.setState({
            messages: { ...state.messages, [channelId]: [...existingMsgs, msg] },
            unreadCounts: {
              ...state.unreadCounts,
              [channelId]: (state.unreadCounts[channelId] || 0) + (state.currentChannelId === channelId ? 0 : 1),
            },
          });
        } 
        else if (eventType === 'DELETE' && oldRow) {
          // Get current state fresh inside the callback
          const currentState = useStore.getState();
          const messageId = oldRow.id as string;
          
          // Find which channel this message belongs to by searching all channels
          let channelId = oldRow.channel_id as string;
          
          // If channel_id isn't in the payload, search for it
          if (!channelId) {
            for (const [chId, msgs] of Object.entries(currentState.messages)) {
              if (msgs.some(m => m.id === messageId)) {
                channelId = chId;
                break;
              }
            }
          }
          
          if (channelId) {
            const channelMessages = currentState.messages[channelId] || [];
            useStore.setState({
              messages: {
                ...currentState.messages,
                [channelId]: channelMessages.filter(m => m.id !== messageId)
              }
            });
          }
        }
      })
      .subscribe();

    subscriptionsRef.current.push({
      unsubscribe: () => supabase!.removeChannel(messagesChannel),
    });

    // 3. Tasks — real-time changes
    const tasksChannel = supabase.channel(`sync-tasks-${teamId}`)
      .on('postgres_changes', {
        event: '*', schema: 'public', table: 'tasks',
        filter: `team_id=eq.${teamId}`,
      }, (payload: { eventType: string; new: any; old: any }) => {
        const { eventType, new: newRow, old: oldRow } = payload;
        const state = useStore.getState();

        if (eventType === 'INSERT' && newRow) {
          if (state.tasks.find(t => t.id === newRow.id)) return;
          useStore.setState({ tasks: [...state.tasks, dbTaskToStore(newRow as Record<string, unknown>)] });
        } else if (eventType === 'UPDATE' && newRow) {
          const updated = dbTaskToStore(newRow as Record<string, unknown>);
          useStore.setState({ tasks: state.tasks.map(t => t.id === newRow.id ? updated : t) });
        } else if (eventType === 'DELETE' && oldRow) {
          useStore.setState({ tasks: state.tasks.filter(t => t.id !== oldRow.id) });
        }
      })
      .subscribe();

    subscriptionsRef.current.push({
      unsubscribe: () => supabase!.removeChannel(tasksChannel),
    });

    // 4. Notifications — real-time INSERT
    const notifChannel = supabase.channel(`sync-notif-${userId}`)
      .on('postgres_changes', {
        event: 'INSERT', schema: 'public', table: 'notifications',
        filter: `user_id=eq.${userId}`,
      }, (payload: { new: any }) => {
        const newRow = payload.new;
        if (!newRow) return;
        const state = useStore.getState();
        if (state.notifications.find(n => n.id === newRow.id)) return;
        const notif = dbNotificationToStore(newRow as Record<string, unknown>);
        useStore.setState({ notifications: [notif, ...state.notifications] });
      })
      .subscribe();

    subscriptionsRef.current.push({
      unsubscribe: () => supabase!.removeChannel(notifChannel),
    });

    // 5. Team members — presence updates
    const presenceChannel = supabase.channel(`sync-presence-${teamId}`)
      .on('postgres_changes', {
        event: 'UPDATE', schema: 'public', table: 'team_members',
        filter: `team_id=eq.${teamId}`,
      }, (payload: { new: any }) => {
        const newRow = payload.new;
        if (!newRow) return;
        const state = useStore.getState();
        useStore.setState({
          team: state.team.map(m =>
            m.id === newRow.user_id
              ? {
                  ...m,
                  presenceStatus: (newRow.status as TeamMember['presenceStatus']) || m.presenceStatus,
                  customStatus: (newRow.custom_status as string) || m.customStatus,
                  lastSeen: (newRow.last_seen as string) || m.lastSeen,
                }
              : m
          ),
        });
      })
      .subscribe();

    subscriptionsRef.current.push({
      unsubscribe: () => supabase!.removeChannel(presenceChannel),
    });

    // 6. Status History — real-time INSERT
    const historyChannel = supabase.channel(`sync-status-history-${teamId}`)
      .on('postgres_changes', {
        event: 'INSERT', schema: 'public', table: 'status_history',
      }, (payload: { new: any }) => {
        const newRow = payload.new;
        if (!newRow) return;
        const state = useStore.getState();
        const leadId = newRow.lead_id;
        
        const lead = state.leads.find(l => l.id === leadId);
        if (lead) {
          const newEntry: StatusHistoryEntry = {
            fromStatus: newRow.from_status || null,
            toStatus: newRow.to_status || 'new',
            timestamp: newRow.changed_at || new Date().toISOString(),
            changedBy: newRow.changed_by || 'System'
          };

          const isDuplicate = lead.statusHistory.some(h => 
            h.toStatus === newEntry.toStatus && 
            Math.abs(new Date(h.timestamp).getTime() - new Date(newEntry.timestamp).getTime()) < 5000
          );

          if (!isDuplicate) {
            useStore.setState({
              leads: state.leads.map(l => 
                l.id === leadId 
                  ? { ...l, statusHistory: [...l.statusHistory, newEntry] } 
                  : l
              )
            });
          }
        }
      })
      .subscribe();

    subscriptionsRef.current.push({
      unsubscribe: () => supabase!.removeChannel(historyChannel),
    });

    // 7. AI Threads — real-time INSERT/UPDATE/DELETE
    const aiThreadsChannel = supabase!.channel(`sync-ai-threads-${userId}`)
      .on('postgres_changes', {
        event: '*', schema: 'public', table: 'ai_threads',
        filter: `user_id=eq.${userId}`,
      }, (payload: { eventType: string; new: any; old: any }) => {
        const { eventType, new: newRow, old: oldRow } = payload;
        const state = useStore.getState();

        if (eventType === 'INSERT' && newRow) {
          if (state.aiThreads.find(t => t.id === newRow.id)) return;
          const thread = dbAiThreadToStore(newRow);
          useStore.setState({
            aiThreads: [thread, ...state.aiThreads].sort((a, b) => {
              if (a.pinned !== b.pinned) return a.pinned ? -1 : 1;
              return new Date(b.lastMessageAt).getTime() - new Date(a.lastMessageAt).getTime();
            }),
            aiMessages: { ...state.aiMessages, [newRow.id]: state.aiMessages[newRow.id] || [] }
          });
        } else if (eventType === 'UPDATE' && newRow) {
          const updated = dbAiThreadToStore(newRow);
          useStore.setState({
            aiThreads: state.aiThreads.map(t => t.id === newRow.id ? { ...t, ...updated } : t).sort((a, b) => {
              if (a.pinned !== b.pinned) return a.pinned ? -1 : 1;
              return new Date(b.lastMessageAt).getTime() - new Date(a.lastMessageAt).getTime();
            }),
          });
        } else if (eventType === 'DELETE' && oldRow) {
          const newMessages = { ...state.aiMessages };
          delete newMessages[oldRow.id];
          const updatedThreads = state.aiThreads.filter(t => t.id !== oldRow.id);
          let newCurrentId = state.currentAiThreadId;
          if (state.currentAiThreadId === oldRow.id) {
            newCurrentId = updatedThreads.length > 0 ? updatedThreads[0].id : null;
          }
          useStore.setState({
            aiThreads: updatedThreads,
            currentAiThreadId: newCurrentId,
            aiMessages: newMessages
          });
        }
      })
      .subscribe();

    subscriptionsRef.current.push({
      unsubscribe: () => supabase!.removeChannel(aiThreadsChannel),
    });

    // 8. AI Messages — real-time INSERT/DELETE
    const aiMessagesChannel = supabase!.channel(`sync-ai-messages-${userId}`)
      .on('postgres_changes', {
        event: '*', schema: 'public', table: 'ai_messages',
        filter: `user_id=eq.${userId}`,
      }, (payload: { eventType: string; new: any; old: any }) => {
        const { eventType, new: newRow, old: oldRow } = payload;
        const state = useStore.getState();

        if (eventType === 'INSERT' && newRow) {
          const threadId = newRow.thread_id as string;
          const threadMessages = state.aiMessages[threadId] || [];
          if (threadMessages.find(m => m.id === newRow.id)) return;
          const msg = dbAiMessageToStore(newRow);
          useStore.setState({
            aiMessages: {
              ...state.aiMessages,
              [threadId]: [...threadMessages, msg]
            }
          });
        } else if (eventType === 'DELETE' && oldRow) {
          const messageId = oldRow.id as string;
          let threadId = oldRow.thread_id as string;
          if (!threadId) {
            for (const [tId, msgs] of Object.entries(state.aiMessages)) {
              if (msgs.some(m => m.id === messageId)) {
                threadId = tId;
                break;
              }
            }
          }
          if (threadId) {
            const threadMessages = state.aiMessages[threadId] || [];
            useStore.setState({
              aiMessages: {
                ...state.aiMessages,
                [threadId]: threadMessages.filter(m => m.id !== messageId)
              }
            });
          }
        }
      })
      .subscribe();

    subscriptionsRef.current.push({
      unsubscribe: () => supabase!.removeChannel(aiMessagesChannel),
    });

    // Set user offline on page unload
    const handleBeforeUnload = () => {
      supabase!.from('team_members')
        .update({ status: 'offline', last_seen: new Date().toISOString() })
        .eq('team_id', teamId)
        .eq('user_id', userId)
        .then(() => {});
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    subscriptionsRef.current.push({
      unsubscribe: () => window.removeEventListener('beforeunload', handleBeforeUnload),
    });
  }

  // ── Render ──────────────────────────────────────────────────────────────────

  // Demo mode — render immediately, seed data is in the store
  if (!isSupabaseConfigured || !supabase) {
    return <>{children}</>;
  }

  // Still loading from Supabase — show DeepSpaceLoader with real progress
  if (loading) {
    return <DeepSpaceLoader progress={syncProgress} status={syncStatus} />;
  }

  return <>{children}</>;
}
